import { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';

/**
 * Custom hook to check subscription status
 * This will call your backend API to verify if the user has an active subscription
 */
export const useSubscription = () => {
  const { user, isLoaded } = useUser();
  const [subscriptionStatus, setSubscriptionStatus] = useState({
    isActive: false,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    const checkSubscription = async () => {
      if (!isLoaded || !user) {
        setSubscriptionStatus({
          isActive: false,
          isLoading: false,
          error: null,
        });
        return;
      }

      // Bypass subscription check for specific emails
      const userEmail = user.primaryEmailAddress?.emailAddress;
      const bypassEmails = ['csrbinov@gmail.com', 'donevmartin06@gmail.com'];
      if (bypassEmails.includes(userEmail)) {
        setSubscriptionStatus({
          isActive: true,
          isLoading: false,
          error: null,
        });
        return;
      }

      try {
        setSubscriptionStatus(prev => ({ ...prev, isLoading: true, error: null }));

        // Call your backend API to check subscription status
        let response;
        try {
          const userEmail = user.primaryEmailAddress?.emailAddress;
          response = await fetch(`/api/check-subscription?email=${encodeURIComponent(userEmail || '')}&userId=${encodeURIComponent(user.id || '')}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              // You may want to send the Clerk session token here
              // 'Authorization': `Bearer ${await user.getToken()}`,
            },
          });
        } catch (networkErr) {
          // Network error - backend not set up yet
          console.warn('Subscription check endpoint not available:', networkErr);
          setSubscriptionStatus({
            isActive: false,
            isLoading: false,
            error: null, // Don't treat missing backend as an error
          });
          return;
        }

        if (!response.ok) {
          // If 404, backend isn't set up - treat as no subscription
          if (response.status === 404) {
            console.warn('Subscription check endpoint not found (404) - backend not set up');
            setSubscriptionStatus({
              isActive: false,
              isLoading: false,
              error: null,
            });
            return;
          }
          throw new Error(`Failed to check subscription status: ${response.statusText}`);
        }

        // Check if response is actually JSON (not HTML error page)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.warn('Subscription check returned non-JSON response - backend may not be set up');
          setSubscriptionStatus({
            isActive: false,
            isLoading: false,
            error: null,
          });
          return;
        }

        const data = await response.json();
        setSubscriptionStatus({
          isActive: data.isActive || false,
          isLoading: false,
          error: null,
        });
      } catch (err) {
        // Only log actual errors, not missing backend
        if (err.message && !err.message.includes('Unexpected token')) {
          console.error('Error checking subscription:', err);
        }
        setSubscriptionStatus({
          isActive: false,
          isLoading: false,
          error: null, // Treat as no subscription, not an error
        });
      }
    };

    checkSubscription();
    
    // Optionally poll for subscription updates (e.g., after payment)
    const interval = setInterval(checkSubscription, 30000); // Check every 30 seconds
    
    return () => clearInterval(interval);
  }, [user, isLoaded]);

  return subscriptionStatus;
};

